import { useEffect, useRef, useState } from "react"

import PropTypes from "prop-types"

import { getCacheBustVersionParam } from "../../helpers/general"

const cacheBustVersion = getCacheBustVersionParam()

const isProduction = process.env.NODE_ENV === "production"

const PictureElement = ({ images, alt, className, style, onLoad, onError, loading, opacity }) => {
  if (!images || images.length <= 1) {
    return (
      <img
        loading={loading}
        onLoad={onLoad}
        style={{ ...style, opacity }}
        src={`${images[0]}.png` + cacheBustVersion}
        className={className}
        alt={alt}
      />
    )
  }

  return (
    <picture
      className={className}
      style={{ ...style, opacity }}
    >
      {images[2] && (
        <source
          media="(max-width: 769px)"
          srcSet={`${images[2]}.webp` + cacheBustVersion}
          type="image/webp"
        />
      )}
      {images[1] && (
        <source
          media="(max-width: 1360px)"
          srcSet={`${images[1]}.webp` + cacheBustVersion}
          type="image/webp"
        />
      )}
      <source
        media="(min-width: 1361px)"
        srcSet={`${images[0]}.webp` + cacheBustVersion}
        type="image/webp"
      />

      {images[2] && (
        <source
          media="(max-width: 769px)"
          srcSet={`${images[2]}.png` + cacheBustVersion}
          type="image/png"
        />
      )}
      {images[1] && (
        <source
          media="(max-width: 1360px)"
          srcSet={`${images[1]}.png` + cacheBustVersion}
          type="image/png"
        />
      )}
      <source
        media="(min-width: 1361px)"
        srcSet={`${images[0]}.png` + cacheBustVersion}
        type="image/png"
      />

      <img
        src={`${images[0]}.png` + cacheBustVersion}
        alt={alt}
        loading={loading}
        onLoad={onLoad}
        onError={onError}
      />
    </picture>
  )
}

//TODO : https://css-tricks.com/preventing-content-reflow-from-lazy-loaded-images/
const LayerImage = ({ alt, className, images, style, on_load, loading }) => {
  const [displayedImages, setDisplayedImages] = useState(images)
  const [nextImages, setNextImages] = useState(null)
  const imagesKey = images?.join("|") || ""
  const prevImagesKeyRef = useRef(imagesKey)

  useEffect(() => {
    if (imagesKey !== prevImagesKeyRef.current) {
      prevImagesKeyRef.current = imagesKey
      // New images arrived — render them hidden, keep old ones visible
      setNextImages(images)
    }
  }, [imagesKey, images])

  const handleNextLoaded = () => {
    // New image is fully loaded — swap it in and remove old
    setDisplayedImages(nextImages)
    setNextImages(null)
    if (on_load) on_load()
  }

  if (
    !images ||
    !Array.isArray(images) ||
    images.length <= 0 ||
    images.join("").length === 0
  ) {
    return ""
  }

  return (
    <>
      {/* Current visible image */}
      <PictureElement
        images={displayedImages}
        alt={alt}
        className={className}
        style={style}
        loading={loading}
        onLoad={!nextImages ? on_load : undefined}
        onError={() =>
          !isProduction ? console.error(`Error loading: ${displayedImages}`) : ""
        }
        opacity={1}
      />
      {/* Next image loading hidden — when loaded it becomes the displayed one */}
      {nextImages && (
        <PictureElement
          images={nextImages}
          alt={alt}
          className={className}
          style={style}
          loading={loading}
          onLoad={handleNextLoaded}
          onError={() => {
            if (!isProduction) console.error(`Error loading: ${nextImages}`)
            // Even on error, swap to avoid stuck state
            handleNextLoaded()
          }}
          opacity={0}
        />
      )}
    </>
  )
}

export default LayerImage

LayerImage.propTypes = {
  alt: PropTypes.string,
  className: PropTypes.object,
  images: PropTypes.array,
  style: PropTypes.object,
  loading: PropTypes.func,
  onLoad: PropTypes.func,
}

LayerImage.defaultProps = {
  alt: null,
  images: null,
}
